/**
 * SwitchPorts -- the site-wide switch PORT TABLE (Switch > Monitor > Ports, `switch-ports`).
 *
 * WHY (owner, 2026-09-14)
 * He put Meraki's "Switch Ports" next to Meridian's 28-tile faceplate and wrote "it is not easy
 * to select multiple ports and name them and or edit them". Meraki's answer is a table: one row
 * per port across the site, a checkbox per row, a filter box with "14 of 3488 switch ports", and
 * one Edit dialog for the selection. This page is that table.
 *
 * ROUTE CHOICE: this REWORKS the existing `switch-ports` section instead of adding a new page.
 * It is already the Switch nav's "Ports" entry, buildSections() already mounts it at org AND
 * site scope, and useDevices() already narrows to the session site -- so at a site it is "every
 * port at this site" and at org scope "every port in the customer". A second page would leave
 * two port screens that disagree. The per-switch cards it replaces carried the same data but no
 * selection across switches; their per-port Advanced editor and the switch-level Voice VLAN /
 * LAG dialogs moved to components/site/SwitchPortDialogs and stay reachable from the toolbar.
 *
 * DATA: one useSwitchDetails() query per switch (the same cache entries the device sheet
 * uses), folded into rows by lib/portTable. Nothing is guessed: a value the switch did not
 * report renders "—", and a switch whose ports cannot be read is NAMED above the table instead
 * of silently contributing zero rows to "N of M ports".
 *
 * VLANS (OP#1819): the VLAN column used to show the native VLAN only. It now shows the native
 * VLAN AND the tagged VLANs the switch itself reports (switch/port-vlans `observed`, read over
 * the switch CLI), with the source next to the numbers: "switch" (read from the device, age on
 * hover), "pushed config", or "untagged only" (no tagged read possible -- SonicFi RSW). An
 * unknown tagged list renders "tagged ?" and never as none. Switches whose VLANs are not read
 * from the device are named above the table with the reason, and org_admin+ get "Read VLANs
 * from switches" (one read-only CLI session per readable switch, one after another).
 *
 * FILTERS live in the URL (?q=&switch=&status=&poe=&vlan=), written with `replace: true` --
 * the Devices-list pattern: a refresh or a shared link returns the same table, and narrowing a
 * list does not flood history. `?switch=<serial>&ports=3,4` is the device sheet's deep link;
 * `ports` preselects once and is then removed from the URL (a selection is not a filter).
 *
 * WRITES are msp_admin only (the backend enforces is_admin on every switch write route). Other
 * roles get the same table with no checkboxes and no toolbar.
 *
 * NAMES (owner decision 2026-09-14, option C "Meraki auto-sync"): the Name column is Meridian's
 * DESIRED name with a sync badge -- Synced / Syncing / Waiting for switch (reason) / Failed (the
 * switch's words, Retry) / Not written to the switch yet (imported before auto-sync). Switches not
 * assigned to a site are never written automatically: their badges offer "Write names to switch".
 * Every role sees the badge; the actions are msp_admin only.
 *
 * DESIGN: Meridian tokens only. The status ramp colours operational state (link up, switch
 * offline, PoE fault); brand marks interactive affordances (selection, links, primary action).
 * Tablet-first: the toolbar and filter row wrap, and the table scrolls horizontally inside its
 * own container (DataGrid reflow=false) rather than becoming 28 cards per switch.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useDevices, usePortVlansMany, useReadPortVlans, useSwitchDetails } from "@/lib/api/hooks";
import { readErrorText } from "@/components/site/PortVlanPanel";
import { deviceReadLine, vlanCell, type DeviceReadSummary } from "@/lib/portVlans";
import { portsCoverageNote, switchKnownOffline } from "@/lib/liveStatus";
import { useSession } from "@/stores/session";
import { DataGrid } from "@/components/data/DataGrid";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { SwitchSettingsDialog } from "@/components/site/SwitchSettingsDialog";
import { BulkPortEditDialog } from "@/components/site/BulkPortEditDialog";
import { NameSyncBadge } from "@/components/site/NameSyncBadge";
import {
  PoeCycleButton,
  PortEditor,
  SwitchLagDialog,
  SwitchVoiceVlanDialog,
} from "@/components/site/SwitchPortDialogs";
import { formatPortRefs } from "@/lib/switchPorts";
import {
  FILTER_KEYS,
  anyPortFilter,
  buildPortRows,
  filterPortRows,
  formatBytes,
  formatLink,
  formatPoe,
  hasTrafficCounters,
  parsePortFilters,
  parsePortsParam,
  portStatus,
  preselectKeys,
  selectClick,
  toggleAllVisible,
  type BulkEdit,
  type PortRow,
} from "@/lib/portTable";
import { cn } from "@/lib/cn";

/** Which switch-level dialog is open (single-switch selection only). */
type SwitchDialog = "settings" | "voice" | "lag" | null;

export default function SwitchPorts() {
  // ------------------------------------------------------------------ URL filters ---------
  const [params, setParams] = useSearchParams();
  /** Set or clear one query parameter without adding a history entry. */
  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const filters = parsePortFilters(params);

  // The search box types locally and writes `q` after a 200 ms debounce (Devices pattern).
  // `wroteQ` tells "our own write landed" from "the URL changed under us" (a link, Back).
  const [searchInput, setSearchInput] = useState(filters.q);
  const wroteQ = useRef(filters.q);
  useEffect(() => {
    const t = setTimeout(() => {
      const value = searchInput.trim();
      if (value !== filters.q) {
        wroteQ.current = value;
        setParam("q", value);
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm on typing only
  }, [searchInput]);
  useEffect(() => {
    if (filters.q !== wroteQ.current) {
      wroteQ.current = filters.q;
      setSearchInput(filters.q);
    }
  }, [filters.q]);

  /** "Clear filters": one URL write for every filter key (and a pending `ports` preselect). */
  const clearFilters = () => {
    wroteQ.current = "";
    setSearchInput("");
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of [...FILTER_KEYS, "ports"]) next.delete(k);
        return next;
      },
      { replace: true },
    );
  };

  // ------------------------------------------------------------------ data ----------------
  // Switches in scope: ?kind=switch is applied SERVER-SIDE (a browser-side filter would only see
  // the 100 most recently contacted devices and drop the quiet switch worth opening this for).
  const { data: switchList, isLoading, error } = useDevices({ kind: "switch" });
  const switches = useMemo(() => switchList?.items ?? [], [switchList]);
  // Every device in scope, only to resolve LLDP neighbours to Meridian devices (link + kind for
  // the uplink guard). One page of 100 by recency: infrastructure is online, so it is in it.
  const { data: deviceList } = useDevices({});
  const details = useSwitchDetails(switches.map((s) => s.serial));
  // useQueries returns a new array every render; key the memo on what actually changed.
  const detailsKey = details.map((q) => `${q.status}:${q.dataUpdatedAt}`).join("|");
  // Per-switch VLAN views (OP#1819): native + the device's tagged list, source, confidence.
  const vlanViews = usePortVlansMany(switches.map((s) => s.serial));
  const vlanKey = vlanViews.map((q) => `${q.status}:${q.dataUpdatedAt}`).join("|");

  const rows = useMemo(
    () =>
      buildPortRows(
        switches.map((d, i) => {
          const detail = details[i]?.data;
          return {
            sw: { serial: d.serial, name: d.name, model: d.model ?? null, online: d.online, uplinkPort: d.uplink?.port ?? null },
            ports: detail?.snmp_ok ? detail.ports : [],
            vlans: vlanViews[i]?.data?.ports ?? null,
          };
        }),
        (deviceList?.items ?? []).map((d) => ({ serial: d.serial, name: d.name, kind: d.kind ?? null, model: d.model ?? null })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailsKey/vlanKey stand in for the query arrays
    [switches, detailsKey, vlanKey, deviceList],
  );

  /** Switches we have no port list for, named so "N of M ports" is not read as the whole site. */
  const unreported = switches.filter((_, i) => {
    const q = details[i];
    return !!q && !q.isLoading && (q.isError || !q.data?.snmp_ok);
  });
  const polling = details.some((q) => q.isLoading);
  // OP#1860: split by the device list's own status. An offline switch is SAID to be offline; only
  // an online switch with no port list keeps the "reporting gap, not an outage" wording.
  const coverage = portsCoverageNote(
    unreported.filter((s) => switchKnownOffline(s)).map((s) => s.name),
    unreported.filter((s) => !switchKnownOffline(s)).map((s) => s.name),
  );

  /** Switches whose port VLANs are NOT a fresh read from the device, named with the reason. */
  const vlanNotes = switches
    .map((sw, i) => ({ sw, dr: vlanViews[i]?.data?.device_read ?? null }))
    .filter((x): x is { sw: (typeof switches)[number]; dr: DeviceReadSummary } => !!x.dr && x.dr.status !== "ok");
  /** Switches a CLI read is possible on (the controller says so; never a SonicFi RSW). */
  const readable = switches.filter((_, i) => vlanViews[i]?.data?.device_read?.can_read);

  const visible = useMemo(() => filterPortRows(rows, filters), [rows, filters.q, filters.switch, filters.status, filters.poe, filters.vlan]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleKeys = visible.map((r) => r.key);

  // ------------------------------------------------------------------ selection -----------
  const canWrite = useSession((s) => s.hasRole("msp_admin"));
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const scopeBase = siteId ? `/o/${orgId}/s/${siteId}` : `/o/${orgId}`;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Shift-range anchor, as a KEY: an index would point at a different row after a sort. */
  const [anchorKey, setAnchorKey] = useState<string | null>(null);
  const selectedRows = rows.filter((r) => selected.has(r.key));
  const hiddenSelected = selectedRows.filter((r) => !visibleKeys.includes(r.key)).length;
  const selectedSerials = [...new Set(selectedRows.map((r) => r.serial))];
  const oneSwitch = selectedSerials.length === 1 ? switches.find((s) => s.serial === selectedSerials[0]) ?? null : null;
  const onePort = selectedRows.length === 1 ? selectedRows[0] ?? null : null;

  // Deep link `?switch=<serial>&ports=3,4`: preselect once that switch's ports have loaded, then
  // drop `ports` from the URL. Tracked by value so a second link while mounted still applies.
  const portsParam = params.get("ports");
  const consumed = useRef<string | null>(null);
  useEffect(() => {
    if (!portsParam) {
      consumed.current = null;
      return;
    }
    const token = `${filters.switch}|${portsParam}`;
    if (consumed.current === token || !filters.switch) return;
    if (!rows.some((r) => r.serial === filters.switch)) return; // wait for that switch's ports
    consumed.current = token;
    setSelected(new Set(preselectKeys(rows, filters.switch, parsePortsParam(portsParam))));
    setParam("ports", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setParam is recreated each render
  }, [rows, portsParam, filters.switch]);

  // ------------------------------------------------------------------ dialogs -------------
  /** Non-null = the bulk Edit dialog is open, seeded with this preset (toolbar quick actions). */
  const [editPreset, setEditPreset] = useState<Partial<BulkEdit> | null>(null);
  const [switchDialog, setSwitchDialog] = useState<SwitchDialog>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ------------------------------------------------------------------ columns -------------
  const cbCls = "h-5 w-5 cursor-pointer accent-brand";
  const showTraffic = hasTrafficCounters(rows);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));

  const columns: ColumnDef<PortRow, unknown>[] = [];
  if (canWrite) {
    columns.push({
      id: "select",
      enableSorting: false,
      header: () => (
        <input
          type="checkbox"
          className={cbCls}
          aria-label="Select all shown ports"
          checked={allVisibleSelected}
          onChange={() => setSelected((prev) => toggleAllVisible(prev, visibleKeys))}
        />
      ),
      cell: (c) => {
        const r = c.row.original;
        return (
          <input
            type="checkbox"
            className={cbCls}
            aria-label={`Select ${r.switchName} port ${r.number}`}
            checked={selected.has(r.key)}
            // All logic in onClick (it carries shiftKey); onChange is a no-op for the controlled input.
            onChange={() => {}}
            onClick={(e) => {
              // The range follows the grid's CURRENT sorted + filtered order -- what the operator sees.
              const keys = c.table.getRowModel().rows.map((x) => x.original.key);
              const anchor = anchorKey ? keys.indexOf(anchorKey) : -1;
              const index = keys.indexOf(r.key);
              setSelected((prev) => selectClick(prev, keys, anchor >= 0 ? anchor : null, index, e.shiftKey));
              setAnchorKey(r.key);
            }}
          />
        );
      },
    });
  }
  columns.push(
    {
      id: "port",
      header: "Switch / Port",
      // Sort by switch, then faceplate order (not lexically: "10" must not sit beside "1").
      accessorFn: (r) => `${r.switchName}\u0000${String(r.order).padStart(4, "0")}`,
      cell: (c) => {
        const r = c.row.original;
        return (
          <div className="flex min-w-[9rem] flex-col leading-tight">
            <span className="flex items-center gap-1.5 text-[11px] text-ink-muted" title={r.serial}>
              <span className="max-w-[14rem] truncate">{r.switchName}</span>
              {!r.switchOnline && <span className="text-warn">offline</span>}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="font-mono font-medium tabular-nums text-ink" title={r.port.name}>
                {r.number}
              </span>
              {r.isUplink && (
                <span className="rounded border border-line px-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                  uplink
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      id: "name",
      header: "Name",
      accessorFn: (r) => r.port.alias || r.port.descr || "",
      cell: (c) => {
        const r = c.row.original;
        const name = r.port.alias || r.port.descr;
        return (
          <div className="flex min-w-[9rem] flex-col items-start gap-1 leading-tight">
            {name ? <span className="whitespace-nowrap">{name}</span> : <span className="text-ink-subtle">—</span>}
            {/* Does the switch hold this name yet? (lib/portNameSync). Absent when unnamed. */}
            <NameSyncBadge serial={r.serial} sync={r.port.name_sync} compact />
          </div>
        );
      },
    },
    {
      id: "vlan",
      header: "VLAN",
      accessorFn: (r) => r.vlans?.observed?.native ?? r.port.native_vlan ?? -1,
      cell: (c) => <VlanTableCell row={c.row.original} />,
    },
    {
      id: "neighbor",
      header: "LLDP neighbour",
      accessorFn: (r) => r.neighborSystem ?? "",
      cell: (c) => <NeighborCell row={c.row.original} scopeBase={scopeBase} />,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (r) => portStatus(r.port),
      cell: (c) => <StatusCell row={c.row.original} />,
    },
    {
      id: "link",
      header: "Link",
      accessorFn: (r) => (r.port.status === "up" ? r.port.speed_mbps ?? 0 : -1),
      cell: (c) => <span className="whitespace-nowrap font-mono text-xs">{formatLink(c.row.original.port)}</span>,
    },
    {
      id: "poe",
      header: "PoE",
      accessorFn: (r) => r.port.poe_power_mw ?? (r.port.poe_enabled ? 0 : -1),
      cell: (c) => {
        const p = c.row.original.port;
        return (
          <span className="whitespace-nowrap text-xs">
            {formatPoe(p)}
            {/* A PoE fault is operational state: warn ramp, with the switch's own words on hover. */}
            {p.poe_fault && <span className="ml-1 text-warn" title={p.poe_fault}>fault</span>}
          </span>
        );
      },
    },
  );
  // Traffic only when the state document really carries counters for this scope.
  if (showTraffic) {
    columns.push(
      {
        id: "tx",
        header: "Sent",
        accessorFn: (r) => r.port.tx_bytes ?? -1,
        cell: (c) => <span className="block text-right font-mono text-xs tabular-nums">{formatBytes(c.row.original.port.tx_bytes)}</span>,
      },
      {
        id: "rx",
        header: "Received",
        accessorFn: (r) => r.port.rx_bytes ?? -1,
        cell: (c) => <span className="block text-right font-mono text-xs tabular-nums">{formatBytes(c.row.original.port.rx_bytes)}</span>,
      },
    );
  }

  // ------------------------------------------------------------------ render --------------
  const filterCls = "min-h-touch rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink";
  const narrowed = anyPortFilter(filters);
  const subtitle = `${visible.length} of ${rows.length} ports · ${switches.length} switch${switches.length === 1 ? "" : "es"}`;
  const sortedSwitches = [...switches].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  return (
    <div>
      <PageHeader title="Switch ports" subtitle={subtitle} />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading switches…</div>}
      {switchList && switches.length === 0 && (
        <EmptyState title="No switches in scope" hint="Onboard a switch to see its ports here." />
      )}

      {switches.length > 0 && (
        <>
          {/* ONE FILTER ROW. Search leads and takes the free width; on a tablet it wraps to its
              own line and the selects flow under it. */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <SearchField value={searchInput} onChange={setSearchInput} />
            <select
              className={filterCls}
              aria-label="Switch"
              value={filters.switch}
              onChange={(e) => setParam("switch", e.target.value)}
            >
              <option value="">All switches</option>
              {sortedSwitches.map((s) => (
                <option key={s.serial} value={s.serial}>{s.name}</option>
              ))}
            </select>
            <select
              className={filterCls}
              aria-label="Status"
              value={filters.status}
              onChange={(e) => setParam("status", e.target.value)}
            >
              <option value="">Any status</option>
              <option value="up">Up</option>
              <option value="down">Down</option>
              <option value="disabled">Disabled</option>
            </select>
            <select
              className={filterCls}
              aria-label="PoE"
              value={filters.poe}
              onChange={(e) => setParam("poe", e.target.value)}
            >
              <option value="">Any PoE</option>
              <option value="on">PoE on</option>
              <option value="off">PoE off</option>
            </select>
            <input
              className={cn(filterCls, "w-24 tabular-nums")}
              aria-label="VLAN"
              placeholder="VLAN"
              inputMode="numeric"
              value={params.get("vlan") ?? ""}
              onChange={(e) => setParam("vlan", e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
            {(narrowed || searchInput) && (
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>

          {/* Honesty about coverage: which switches contribute no rows, and why that is not "no ports". */}
          {(coverage.length > 0 || polling) && (
            <p className="mb-3 text-xs text-ink-muted" data-testid="ports-coverage">
              {polling && "Reading ports… "}
              {coverage.join(" ")}
            </p>
          )}

          {/* Where the VLAN column comes from, per switch (OP#1819): read from the switch, or why
              not. Plus the explicit read for org_admin+. */}
          <VlanSourcesNote notes={vlanNotes} readable={readable} />

          {/* BULK TOOLBAR (msp_admin). Rendered whenever there are ports so the actions are
              discoverable; disabled until something is selected. Hidden when NO port is listed
              at all (every switch offline / unreported): an Edit toolbar over an empty table
              read as "Edit is enabled with 0 ports" (OP#1860). Wraps on a tablet. */}
          {rows.length > 0 && (
          <RoleGate min="msp_admin" mode="hide">
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-2 p-2">
              <span className="px-1 text-sm text-ink">
                {selected.size === 0 ? (
                  <span className="text-ink-muted">Select ports to edit</span>
                ) : (
                  <>
                    <span className="font-medium tabular-nums">{selected.size}</span> selected
                    {hiddenSelected > 0 && (
                      // A bulk edit reaches every selected port, including ones a filter hides.
                      <span className="text-warn"> ({hiddenSelected} hidden by filters)</span>
                    )}
                    {" · "}
                    <button
                      type="button"
                      className="font-medium text-brand hover:underline"
                      onClick={() => setSelected(new Set())}
                    >
                      clear
                    </button>
                  </>
                )}
              </span>
              <Button variant="primary" disabled={!selectedRows.length} onClick={() => setEditPreset({})}>
                Edit…
              </Button>
              <Button variant="secondary" disabled={!selectedRows.length} onClick={() => setEditPreset({ enabled: "up" })}>
                Enable
              </Button>
              <Button variant="secondary" disabled={!selectedRows.length} onClick={() => setEditPreset({ enabled: "down" })}>
                Disable
              </Button>
              <Button variant="secondary" disabled={!selectedRows.length} onClick={() => setEditPreset({ poe: "on" })}>
                PoE on
              </Button>
              <Button variant="secondary" disabled={!selectedRows.length} onClick={() => setEditPreset({ poe: "off" })}>
                PoE off
              </Button>
              {/* Switch-level actions only make sense for exactly one switch. */}
              {oneSwitch && (
                <>
                  <span className="mx-1 hidden h-6 w-px bg-line sm:inline-block" aria-hidden />
                  <Button variant="secondary" onClick={() => setSwitchDialog("settings")}>
                    Switch settings…
                  </Button>
                  <Button variant="ghost" onClick={() => setSwitchDialog("voice")}>
                    Voice VLAN…
                  </Button>
                  <Button variant="ghost" onClick={() => setSwitchDialog("lag")}>
                    Link aggregation…
                  </Button>
                </>
              )}
              {/* One port: the full per-port editor (rate limits, 802.1p, voice VLAN) and PoE cycle. */}
              {onePort && (
                <>
                  <Button variant="ghost" onClick={() => setAdvancedOpen(true)}>
                    Advanced…
                  </Button>
                  {onePort.port.poe_enabled && !onePort.isUplink && (
                    <span className="px-2 text-sm">
                      <PoeCycleButton serial={onePort.serial} port={onePort.port.name} />
                    </span>
                  )}
                </>
              )}
            </div>
          </RoleGate>
          )}

          <DataGrid<PortRow>
            data={visible}
            columns={columns}
            rowKey={(r) => r.key}
            reflow={false}
            dense
            isLoading={polling && rows.length === 0}
            rowClassName={(r) => selected.has(r.key) && "bg-brand-soft/60"}
            renderCard={(r) => <span>{r.switchName} {r.number}</span>}
            empty={
              rows.length === 0
                ? "No port data reported by the switches in scope yet."
                : "No ports match the current filters."
            }
          />
        </>
      )}

      {/* ------------------------------------------------------------------ dialogs --------- */}
      {editPreset && selectedRows.length > 0 && (
        <RoleGate min="msp_admin" mode="hide">
          <BulkPortEditDialog
            rows={selectedRows}
            preset={editPreset}
            onClose={() => setEditPreset(null)}
            onExclude={(keys) =>
              setSelected((prev) => {
                const next = new Set(prev);
                for (const k of keys) next.delete(k);
                return next;
              })
            }
          />
        </RoleGate>
      )}
      {oneSwitch && switchDialog === "settings" && (
        <RoleGate min="msp_admin" mode="hide">
          <SwitchSettingsDialog serial={oneSwitch.serial} switchName={oneSwitch.name} onClose={() => setSwitchDialog(null)} />
        </RoleGate>
      )}
      {oneSwitch && switchDialog === "voice" && (
        <RoleGate min="msp_admin" mode="hide">
          <SwitchVoiceVlanDialog serial={oneSwitch.serial} onClose={() => setSwitchDialog(null)} />
        </RoleGate>
      )}
      {oneSwitch && switchDialog === "lag" && (
        <RoleGate min="msp_admin" mode="hide">
          <SwitchLagDialog serial={oneSwitch.serial} onClose={() => setSwitchDialog(null)} />
        </RoleGate>
      )}
      {onePort && advancedOpen && (
        <RoleGate min="msp_admin" mode="hide">
          <PortEditor
            key={onePort.key}
            serial={onePort.serial}
            port={onePort.port}
            vlans={onePort.vlans ?? null}
            onClose={() => setAdvancedOpen(false)}
          />
        </RoleGate>
      )}
    </div>
  );
}

/**
 * VLAN cell: native, then the tagged VLANs the DEVICE reports, then the source tag. "tagged ?"
 * when the source cannot say (state report only). A drift dot when the switch disagrees with
 * Meridian's plan; the full story (age, confidence, drift text) is on hover.
 */
function VlanTableCell({ row }: { row: PortRow }) {
  const cell = vlanCell(row.vlans, row.port.native_vlan);
  const MAX = 4;
  const shown = cell.tagged?.slice(0, MAX) ?? [];
  const more = (cell.tagged?.length ?? 0) - shown.length;
  return (
    <span className="flex min-w-[7rem] flex-col leading-tight" title={cell.title}>
      <span className="flex items-center gap-1 whitespace-nowrap font-mono text-sm tabular-nums">
        {cell.drift && <span aria-label="differs from Meridian" className="inline-block h-1.5 w-1.5 rounded-full bg-warn" />}
        {cell.native}
        {cell.tagged && cell.tagged.length > 0 && (
          <span className="text-xs text-ink-muted">
            {" + "}
            {shown.join(" ")}
            {more > 0 && ` +${more}`}
          </span>
        )}
        {cell.tagged == null && cell.native !== "—" && <span className="text-xs text-ink-subtle"> + tagged ?</span>}
      </span>
      <span className={cell.confidence === "high" ? "text-[11px] text-ink-subtle" : "text-[11px] text-warn"}>
        {cell.source}
      </span>
    </span>
  );
}

/**
 * Per-switch VLAN source notes and the read action. Renders nothing when every switch's VLANs
 * are a fresh device read. "Read VLANs from switches" walks the readable switches ONE AT A
 * TIME (each is a CLI session through the site gateway; parallel sessions would contend for the
 * same relay) and reports failures by switch.
 */
function VlanSourcesNote({
  notes,
  readable,
}: {
  notes: { sw: { serial: string; name: string }; dr: DeviceReadSummary }[];
  readable: { serial: string; name: string }[];
}) {
  const read = useReadPortVlans();
  const [running, setRunning] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  if (!notes.length && !readable.length) return null;
  const readAll = async () => {
    const failed: string[] = [];
    for (const sw of readable) {
      setRunning(sw.name);
      try {
        await read.mutateAsync(sw.serial);
      } catch (e) {
        failed.push(`${sw.name}: ${readErrorText(e)}`);
      }
    }
    setRunning(null);
    setErrors(failed);
  };
  return (
    <div className="mb-3 flex flex-col gap-1 text-xs" data-testid="vlan-sources">
      {notes.map(({ sw, dr }) => {
        const line = deviceReadLine(dr);
        return (
          <p key={sw.serial} className={line.tone === "warn" ? "text-warn" : "text-ink-muted"}>
            <span className="font-medium text-ink">{sw.name}</span> — {line.text}
          </p>
        );
      })}
      {readable.length > 0 && (
        <RoleGate min="org_admin" mode="hide">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" disabled={running !== null} onClick={() => void readAll()}>
              {running ? `Reading ${running}…` : `Read VLANs from switch${readable.length === 1 ? "" : "es"}`}
            </Button>
            <span className="text-[11px] text-ink-subtle">
              Read-only: show vlan + show interfaces switchport on {readable.length} switch{readable.length === 1 ? "" : "es"}. Nothing is written.
            </span>
          </div>
        </RoleGate>
      )}
      {errors.map((e) => (
        <p key={e} role="alert" className="text-critical">{e}</p>
      ))}
    </div>
  );
}

/** LLDP neighbour: linked to the Meridian device when it resolved, plain text otherwise. */
function NeighborCell({ row, scopeBase }: { row: PortRow; scopeBase: string }) {
  if (!row.neighborSystem && !row.neighborPort) return <span className="text-ink-subtle">—</span>;
  const remote = row.neighborPort ? formatPortRefs(row.neighborPort) : null;
  const full = formatPortRefs(row.port.neighbor);
  return (
    <span className="flex max-w-[18rem] flex-col leading-tight" title={full}>
      {row.neighborDevice ? (
        <Link to={`${scopeBase}/devices/${row.neighborDevice.serial}`} className="truncate font-medium text-brand hover:underline">
          {row.neighborDevice.name}
        </Link>
      ) : (
        <span className="truncate">{row.neighborSystem ?? "—"}</span>
      )}
      {remote && <span className="truncate font-mono text-[11px] text-ink-muted">{remote}</span>}
    </span>
  );
}

/** Up / Down / Disabled on the status ramp. Down is not a fault, so it stays neutral. */
function StatusCell({ row }: { row: PortRow }) {
  const status = portStatus(row.port);
  const stp = row.port.stp_state;
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      {status === "up" ? (
        <StatusPill status="healthy">Up</StatusPill>
      ) : (
        <StatusPill status="unknown" hollow>
          {status === "disabled" ? "Disabled" : "Down"}
        </StatusPill>
      )}
      {/* A link that is up but not forwarding is the loop symptom worth seeing in a list. */}
      {status === "up" && stp && !/^forward/i.test(stp) && (
        <span className="text-[11px] text-warn" title={`Spanning tree: ${stp}${row.port.stp_role ? ` (${row.port.stp_role})` : ""}`}>
          STP {stp.toLowerCase()}
        </span>
      )}
    </span>
  );
}

/** Search box over switch, port, name, neighbour and VLAN. */
function SearchField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative min-w-[14rem] flex-1 basis-64">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search ports by switch, port, name, neighbour or VLAN"
        placeholder="Search switch, port, name, neighbour, vlan 100…"
        className={
          "min-h-touch w-full rounded-md border border-line bg-surface py-1.5 pl-9 pr-10 text-sm text-ink " +
          "placeholder:text-ink-subtle [&::-webkit-search-cancel-button]:appearance-none"
        }
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="absolute right-0 top-1/2 flex h-11 w-10 -translate-y-1/2 items-center justify-center text-ink-subtle hover:text-ink"
        >
          <span aria-hidden="true" className="text-lg leading-none">×</span>
        </button>
      )}
    </div>
  );
}
